import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import {
	Navigate,
	RouterProvider,
	// createBrowserRouter,
	createHashRouter,
} from 'react-router-dom';

import Layout from './Layout';
import RequireAuth from './components/RequireAuth';
import Assessment, { CompletedAssessments } from './pages/Assessment';
import AvailableOrganizations, {
	organizationsLoader,
} from './pages/AvailableOrganizations';
import ChangePassword, { changePasswordAction } from './pages/ChangePassword';
import CheckProfile, { checkProfileLoader } from './pages/CheckProfile';
import CompleteRegistration from './pages/CompleteRegistration';
import ErrorPage from './pages/Error';
import ForgotPassword, {
	handleForgotPassword,
	questionsLoader,
} from './pages/ForgotPassword';
import Home from './pages/Home';
import Login, { action as loginAction } from './pages/Login';
import Logout from './pages/Logout';
import OrganizationServices from './pages/OrganizationServices';
import PersonalFinances from './pages/PersonalFinances';
import Profile from './pages/Profile';
import Register, { action as registerAction } from './pages/Register';
import ResetPassword, { resetPasswordAction } from './pages/ResetPassword';
import ServicesRequested, {
	loader as servicesLoader,
} from './pages/ServicesRequested';
import SubmitRequest, {
	SubmitRequestAction,
	SubmitRequestLoader,
} from './pages/SubmitRequest';
import UpdateReferralStatus from './pages/UpdateReferralStatus/UpdateReferralStatus';
import ZipCodeSearch from './pages/ZipCodeSearch';

axios.defaults.baseURL = process.env.BASE_URL;

// Create a client
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false, // default: true
			retry: false, // default: 3
		},
	},
});

const router = createHashRouter(
	[
		{
			element: <Layout />,
			errorElement: <ErrorPage />,
			children: [
				{
					path: '/cca',
					element: <UpdateReferralStatus />,
					errorElement: <ErrorPage />,
				},
				{
					path: '/',
					element: <ZipCodeSearch />,
					errorElement: <ErrorPage />,
				},
				{
					path: '/organizations',
					element: <AvailableOrganizations />,
					errorElement: <ErrorPage />,
					loader: organizationsLoader,
				},
				{
					path: '/orgServices/:orgId',
					element: <OrganizationServices />,
					errorElement: <ErrorPage />,
				},
				{
					path: '/checkProfile',
					element: <CheckProfile />,
					errorElement: <ErrorPage />,
					loader: checkProfileLoader,
				},
				{
					path: '/submitRequest',
					element: <SubmitRequest />,
					errorElement: <ErrorPage />,
					loader: SubmitRequestLoader,
					action: SubmitRequestAction,
				},
				{
					path: '/servicesRequested',
					element: <ServicesRequested />,
					errorElement: <ErrorPage />,
					loader: servicesLoader,
				},
				{
					path: '/home',
					element: (
						<RequireAuth>
							<Home />
						</RequireAuth>
					),
					errorElement: <ErrorPage />,
				},
				{
					path: '/login',
					action: loginAction,
					element: <Login />,
					errorElement: <ErrorPage />,
				},
				{
					path: '/register',
					action: registerAction,
					element: <Register />,
					errorElement: <ErrorPage />,
				},
				{
					path: '/forgotPassword/:subTask?',
					element: <ForgotPassword />,
					errorElement: <ErrorPage />,
					loader: questionsLoader,
					action: handleForgotPassword,
				},
				{
					path: '/forgotPassword/reset',
					element: <ResetPassword />,
					errorElement: <ErrorPage />,
					action: resetPasswordAction,
				},
				{
					path: '/changePassword',
					element: <ChangePassword />,
					errorElement: <ErrorPage />,
					action: changePasswordAction,
				},
				{
					path: '/profile',
					element: (
						<RequireAuth>
							<Profile />
						</RequireAuth>
					),
					errorElement: <ErrorPage />,
				},
				{
					path: '/personalFinances',
					element: (
						<RequireAuth>
							<PersonalFinances />
						</RequireAuth>
					),
					errorElement: <ErrorPage />,
				},
				{
					path: '/assessments',
					element: (
						<RequireAuth>
							<CompletedAssessments />
						</RequireAuth>
					),
					errorElement: <ErrorPage />,
				},
				{
					path: '/assessments/:formId',
					element: (
						<RequireAuth>
							<Assessment />
						</RequireAuth>
					),
					errorElement: <ErrorPage />,
				},
				{
					path: '/completeRegistration',
					element: (
						<RequireAuth>
							<CompleteRegistration />
						</RequireAuth>
					),
					errorElement: <ErrorPage />,
				},
			],
		},
		{ element: <Logout />, path: '/logout' },
		{
			element: <Navigate to="/" />,
			path: '*',
		},
	],
	// { basename: '/test_dir1/socare' },
);

// Add a response interceptor
axios.interceptors.response.use(
	function (response) {
		return response;
	},
	function (error) {
		if (
			error?.config?.headers?.Authorization &&
			401 === error?.response?.status
		) {
			router.navigate('/login');
		} else {
			return Promise.reject(error);
		}
	},
);

const App = () => {
	return (
		<QueryClientProvider client={queryClient}>
			<RouterProvider router={router} />
		</QueryClientProvider>
	);
};

export default App;
